import { seoLinks, absoluteUrl, ogImage } from "@/lib/seo";
import { createFileRoute } from "@tanstack/react-router";

import { CtaBand, PageHero, Prose } from "../../components/site/Page";
import spainmundo from "../../assets/sponsors/spainmundo.png";
import agreaterstar from "../../assets/sponsors/agreaterstar.png";
import mett from "../../assets/sponsors/mett.png";
import theflag from "../../assets/sponsors/theflag.png";
import spisa from "../../assets/sponsors/spisa.png";
import middagskassen from "../../assets/sponsors/middagskassen.png";

const title = "Patrocinadores | Swedish International School Estepona";
const description =
  "Los patrocinadores del colegio hacen posibles experiencias extra para el alumnado. Todos los ingresos van directamente a los alumnos y a sus actividades.";

export const Route = createFileRoute("/es/patrocinadores")({
  head: () => ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "article" },
      { property: "og:url", content: absoluteUrl("/es/patrocinadores") },
      { name: "twitter:card", content: "summary_large_image" },
      { property: "og:locale", content: "es_ES" },
      ...ogImage(),
    ],
    links: seoLinks("/es/patrocinadores"),
  }),
  component: Patrocinadores,
});

const sponsors = [
  {
    name: "Spainmundo",
    logo: spainmundo,
    url: "https://www.spainmundo.com/",
    text: "Múdate con confianza con Spainmundo, tu socio para una mudanza a España sencilla y sin complicaciones. Nuestro equipo de expertos en reubicación internacional se ocupa de todo, desde los trámites y la vivienda hasta la adaptación cultural.",
  },
  {
    name: "A Greater Star",
    logo: agreaterstar,
    url: "https://greaterstar.io",
    text: "Descubre las infinitas posibilidades del mundo digital con A Greater Star, tu socio de confianza en diseño web y marketing digital, con experiencia en desarrollo web, SEO y estrategia digital.",
  },
  {
    name: "METT",
    logo: mett,
    text: "El lifestyle property de 5 estrellas METT Hotel & Beach Resort cuenta con 249 elegantes habitaciones y suites alrededor de la piscina climatizada de Azure Beach, con vistas ininterrumpidas al mar Mediterráneo.",
  },
  {
    name: "The Flag",
    logo: theflag,
    text: "THE FLAG Costa del Sol, en Estepona cerca de Marbella, te invita a desconectar, ya sea para disfrutar de unas vacaciones relajadas o para cuidarte de forma activa con sus expertos en bienestar y su programa holístico.",
  },
  {
    name: "Spisa",
    logo: spisa,
    url: "http://www.spisa.shop/",
    text: "Spisa foodmarket Marbella, Estepona. Alimentación escandinava, delicatessen, panadería y catering. Abierto de 9:00 a 21:00, los 365 días del año.",
  },
  {
    name: "Middagskassen.se",
    logo: middagskassen,
    url: "https://www.middagskassen.se/",
    text: "Middagskassen.se compara los servicios de cajas de comida más grandes y populares de Suecia y te ayuda a encontrar la solución perfecta para un día a día sin estrés.",
  },
];

function Patrocinadores() {
  return (
    <>
      <PageHero
        eyebrow="Colaboraciones"
        title="Patrocinadores"
        intro="El colegio colabora con empresas que ayudan a que el alumnado pueda vivir experiencias especiales. Todos los ingresos de los patrocinadores van directamente a los alumnos o a alguno de nuestros encuentros para familias."
      />

      <Prose>
        <p>
          Gracias al apoyo y las aportaciones de los patrocinadores, el colegio puede ofrecer, entre
          otras cosas, medios técnicos u otros recursos que benefician la escolarización y el día a
          día del alumnado. El colegio es un punto de encuentro natural y varias veces al año todas
          las familias pueden verse también fuera del horario escolar; como patrocinador puedes
          ayudar a que eso ocurra.
        </p>
        <p>
          ¿Quiere vuestra empresa ser patrocinadora? Escribidnos a{" "}
          <a href="mailto:info@sweintschool.com">info@sweintschool.com</a>.
        </p>

        <h2>Para qué se usa el patrocinio</h2>
        <p>
          Los ingresos de los patrocinadores se destinan a la escolarización del alumnado y a los
          encuentros comunes del colegio: material, equipamiento o los costes de los eventos para
          familias. El colegio es una organización pequeña y este tipo de apoyo marca una diferencia
          real en el día a día.
        </p>

        <h2>Ser colaborador</h2>
        <p>
          Como patrocinador, vuestra empresa entra a formar parte de una comunidad de familias de
          varios países que viven y trabajan entre Estepona y Marbella. La colaboración varía según
          lo que queráis aportar: apoyo económico, productos, servicios o participación en un evento.
          Con gusto hablamos de lo que mejor encaje con vuestra empresa.
        </p>
      </Prose>

      <div className="mx-auto grid max-w-5xl gap-6 px-5 pb-16 sm:grid-cols-2">
        {sponsors.map((s) => (
          <article key={s.name} className="rounded-xl border border-border bg-card p-7 shadow-soft">
            <div className="flex h-20 items-center">
              <img
                src={s.logo}
                alt={`Logotipo de ${s.name}`}
                loading="lazy"
                className="max-h-16 w-auto max-w-[220px] object-contain"
              />
            </div>
            <h2 className="mt-5 text-lg font-semibold">{s.name}</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{s.text}</p>
            {s.url && (
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-block text-sm text-primary link-quiet"
              >
                Visitar la web
              </a>
            )}
          </article>
        ))}
      </div>

      <CtaBand locale="es" />
      <div className="h-20" />
    </>
  );
}
